import React, { useState, useEffect } from 'react';
import { apiFetch } from '../../api';

// EU/EEA + UK countries available on TED — codes are ISO 3166-1 alpha-3 as required by TED API
const EU_COUNTRIES = [
  { code: 'AUT', name: 'Austria' },
  { code: 'BEL', name: 'Belgium' },
  { code: 'BGR', name: 'Bulgaria' },
  { code: 'CYP', name: 'Cyprus' },
  { code: 'CZE', name: 'Czech Republic' },
  { code: 'DEU', name: 'Germany' },
  { code: 'DNK', name: 'Denmark' },
  { code: 'EST', name: 'Estonia' },
  { code: 'ESP', name: 'Spain' },
  { code: 'FIN', name: 'Finland' },
  { code: 'FRA', name: 'France' },
  { code: 'GRC', name: 'Greece' },
  { code: 'HRV', name: 'Croatia' },
  { code: 'HUN', name: 'Hungary' },
  { code: 'IRL', name: 'Ireland' },
  { code: 'ISL', name: 'Iceland' },
  { code: 'ITA', name: 'Italy' },
  { code: 'LIE', name: 'Liechtenstein' },
  { code: 'LTU', name: 'Lithuania' },
  { code: 'LUX', name: 'Luxembourg' },
  { code: 'LVA', name: 'Latvia' },
  { code: 'MLT', name: 'Malta' },
  { code: 'NLD', name: 'Netherlands' },
  { code: 'NOR', name: 'Norway' },
  { code: 'POL', name: 'Poland' },
  { code: 'PRT', name: 'Portugal' },
  { code: 'ROU', name: 'Romania' },
  { code: 'SWE', name: 'Sweden' },
  { code: 'SVN', name: 'Slovenia' },
  { code: 'SVK', name: 'Slovakia' },
  { code: 'GBR', name: 'United Kingdom' },
];

function formatDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-GB', {
    day: 'numeric', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function duration(start, end) {
  if (!start || !end) return '—';
  const ms = new Date(end) - new Date(start);
  const s = Math.round(ms / 1000);
  return s < 60 ? `${s}s` : `${Math.round(s / 60)}m ${s % 60}s`;
}

const s = {
  page: { maxWidth: 960, margin: '32px auto', padding: '0 24px 60px' },
  heading: { fontSize: 24, fontWeight: 700, color: '#0f172a', marginBottom: 6 },
  sub: { fontSize: 14, color: '#64748b', marginBottom: 24 },

  // stats bar
  statsBar: {
    display: 'flex', gap: 12, marginBottom: 24, flexWrap: 'wrap',
  },
  statCard: {
    flex: '1 1 140px', background: '#fff', border: '1px solid #e2e8f0',
    borderRadius: 10, padding: '12px 16px',
  },
  statLabel: { fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 },
  statValue: { fontSize: 22, fontWeight: 700, color: '#0f172a' },

  // trigger card
  card: {
    background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12,
    padding: '20px 24px', marginBottom: 24,
  },
  cardTitle: { fontSize: 13, fontWeight: 700, color: '#374151', marginBottom: 14 },
  row: { display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 12 },
  label: { fontSize: 13, color: '#374151', fontWeight: 500, minWidth: 90 },
  numInput: {
    padding: '6px 10px', border: '1px solid #e2e8f0', borderRadius: 7,
    fontSize: 13, width: 72, textAlign: 'center',
  },
  hint: { fontSize: 12, color: '#94a3b8' },

  // country selector
  countryGrid: {
    display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(148px, 1fr))',
    gap: 6, marginTop: 8,
  },
  countryChip: (selected) => ({
    display: 'flex', alignItems: 'center', gap: 6,
    padding: '5px 10px', borderRadius: 6, cursor: 'pointer', userSelect: 'none',
    fontSize: 12, fontWeight: selected ? 600 : 400,
    background: selected ? '#dbeafe' : '#f8fafc',
    color: selected ? '#1d4ed8' : '#374151',
    border: `1px solid ${selected ? '#93c5fd' : '#e2e8f0'}`,
    transition: 'all 0.1s',
  }),
  selectAllBtn: {
    fontSize: 12, color: '#2563eb', background: 'none', border: 'none',
    cursor: 'pointer', padding: '4px 0', fontWeight: 500, textDecoration: 'underline',
  },
  selectedSummary: {
    fontSize: 12, color: '#64748b', marginTop: 8,
  },

  // buttons
  btn: (variant, disabled) => {
    const base = {
      border: 'none', fontWeight: 600, fontSize: 13, padding: '7px 16px',
      borderRadius: 7, cursor: disabled ? 'not-allowed' : 'pointer', transition: 'opacity 0.1s',
    };
    if (variant === 'primary')   return { ...base, background: disabled ? '#93c5fd' : '#2563eb', color: '#fff' };
    if (variant === 'secondary') return { ...base, background: '#f1f5f9', color: '#374151' };
    if (variant === 'outline')   return { ...base, background: '#fff', color: '#2563eb', border: '1px solid #93c5fd' };
    return base;
  },

  // TED check result
  tedResult: (ok) => ({
    display: 'inline-flex', alignItems: 'center', gap: 6,
    fontSize: 13, fontWeight: 500,
    color: ok ? '#15803d' : '#b91c1c',
    background: ok ? '#f0fdf4' : '#fef2f2',
    border: `1px solid ${ok ? '#bbf7d0' : '#fecaca'}`,
    borderRadius: 7, padding: '5px 12px',
  }),

  msg: (ok) => ({ fontSize: 13, fontWeight: 500, color: ok ? '#16a34a' : '#b91c1c' }),
  divider: { height: 1, background: '#f1f5f9', margin: '14px 0' },

  table: {
    width: '100%', borderCollapse: 'collapse', background: '#fff',
    borderRadius: 12, overflow: 'hidden', border: '1px solid #e2e8f0',
  },
  th: {
    fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase',
    letterSpacing: '0.06em', padding: '10px 16px', textAlign: 'left',
    background: '#f8fafc', borderBottom: '1px solid #e2e8f0',
  },
  td: {
    fontSize: 13, color: '#374151', padding: '12px 16px',
    borderBottom: '1px solid #f1f5f9', verticalAlign: 'middle',
  },
  pill: (status) => ({
    fontSize: 11, fontWeight: 600, padding: '2px 9px', borderRadius: 20,
    background: status === 'completed' ? '#dcfce7' : status === 'running' ? '#dbeafe' : '#fee2e2',
    color: status === 'completed' ? '#15803d' : status === 'running' ? '#1d4ed8' : '#b91c1c',
  }),
  empty: { fontSize: 13, color: '#94a3b8', padding: '24px 16px', textAlign: 'center' },
};

export default function AdminIngestion() {
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);

  // ingest controls
  const [daysBack, setDaysBack] = useState(1);
  const [maxTenders, setMaxTenders] = useState(20);
  const [selectedCountries, setSelectedCountries] = useState([]); // empty = all

  // TED availability check
  const [tedChecking, setTedChecking] = useState(false);
  const [tedResult, setTedResult] = useState(null); // { total, error }

  // ingest trigger
  const [ingesting, setIngesting] = useState(false);
  const [msg, setMsg] = useState(null);

  function loadRuns() {
    return apiFetch('/admin/ingestion')
      .then((d) => setRuns(d || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }

  function loadStats() {
    return apiFetch('/admin/stats')
      .then((d) => setStats(d))
      .catch(() => {});
  }

  useEffect(() => {
    loadRuns();
    loadStats();
  }, []);

  function toggleCountry(code) {
    setSelectedCountries((prev) =>
      prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]
    );
    setTedResult(null);
  }

  function selectAll() {
    setSelectedCountries([]);
    setTedResult(null);
  }

  function selectNone() {
    setSelectedCountries(EU_COUNTRIES.map((c) => c.code));
    setTedResult(null);
  }

  const allSelected = selectedCountries.length === 0;

  async function checkTed() {
    setTedChecking(true);
    setTedResult(null);
    try {
      const params = new URLSearchParams({ days_back: daysBack });
      if (!allSelected) selectedCountries.forEach((c) => params.append('countries', c));
      const res = await apiFetch(`/admin/ted-available?${params}`);
      setTedResult({ total: res.total });
    } catch (e) {
      setTedResult({ error: e.message });
    } finally {
      setTedChecking(false);
    }
  }

  async function triggerIngest() {
    setIngesting(true);
    setMsg(null);
    try {
      const params = new URLSearchParams({ days_back: daysBack, max_tenders: maxTenders });
      if (!allSelected) selectedCountries.forEach((c) => params.append('countries', c));
      const res = await apiFetch(`/admin/ingest?${params}`, { method: 'POST' });
      setMsg({ text: res.message || 'Ingestion started — refresh in a minute to see the run.', ok: true });
    } catch (e) {
      setMsg({ text: 'Error: ' + e.message, ok: false });
    } finally {
      setIngesting(false);
    }
  }

  const countryLabel = allSelected
    ? 'All countries'
    : selectedCountries.length === 1
      ? EU_COUNTRIES.find((c) => c.code === selectedCountries[0])?.name
      : `${selectedCountries.length} countries`;

  return (
    <div style={s.page}>
      <h1 style={s.heading}>Ingestion</h1>
      <p style={s.sub}>TED API → Supabase → Pinecone pipeline runs</p>

      {/* Stats bar */}
      {stats && (
        <div style={s.statsBar}>
          <div style={s.statCard}>
            <div style={s.statLabel}>Total in DB</div>
            <div style={s.statValue}>{stats.total_tenders?.toLocaleString() ?? '—'}</div>
          </div>
          <div style={s.statCard}>
            <div style={s.statLabel}>Embedded</div>
            <div style={s.statValue}>{stats.embedded_tenders?.toLocaleString() ?? '—'}</div>
          </div>
          <div style={s.statCard}>
            <div style={s.statLabel}>Not embedded</div>
            <div style={s.statValue}>
              {stats.total_tenders != null && stats.embedded_tenders != null
                ? (stats.total_tenders - stats.embedded_tenders).toLocaleString()
                : '—'}
            </div>
          </div>
          <div style={s.statCard}>
            <div style={s.statLabel}>Users</div>
            <div style={s.statValue}>{stats.total_users?.toLocaleString() ?? '—'}</div>
          </div>
          <div style={s.statCard}>
            <div style={s.statLabel}>Pending matches</div>
            <div style={s.statValue}>{stats.pending_matches?.toLocaleString() ?? '—'}</div>
          </div>
        </div>
      )}

      {/* Trigger card */}
      <div style={s.card}>
        <div style={s.cardTitle}>Run ingestion</div>

        {/* Days back + max tenders */}
        <div style={s.row}>
          <span style={s.label}>Days back</span>
          <input
            type="number" min="1" max="90" value={daysBack}
            onChange={(e) => { setDaysBack(Number(e.target.value)); setTedResult(null); }}
            style={s.numInput}
          />
          <span style={s.hint}>days</span>

          <span style={{ ...s.label, marginLeft: 16 }}>Max tenders</span>
          <input
            type="number" min="0" max="1000" value={maxTenders}
            onChange={(e) => setMaxTenders(Number(e.target.value))}
            style={s.numInput}
          />
          <span style={s.hint}>0 = unlimited (cap: 1000)</span>
        </div>

        <div style={s.divider} />

        {/* Country selector */}
        <div style={{ marginBottom: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 6 }}>
            <span style={s.label}>Countries</span>
            <button style={s.selectAllBtn} onClick={selectAll}>All</button>
            <button style={s.selectAllBtn} onClick={selectNone}>None (pick manually)</button>
          </div>

          {!allSelected && (
            <div style={s.countryGrid}>
              {EU_COUNTRIES.map(({ code, name }) => (
                <div
                  key={code}
                  style={s.countryChip(selectedCountries.includes(code))}
                  onClick={() => toggleCountry(code)}
                >
                  <span>{code}</span>
                  <span style={{ color: 'inherit', opacity: 0.7 }}>{name}</span>
                </div>
              ))}
            </div>
          )}

          <div style={s.selectedSummary}>
            {allSelected
              ? 'Ingesting from all available TED countries'
              : selectedCountries.length === 0
                ? 'No countries selected — ingestion will be empty'
                : `Selected: ${selectedCountries.join(', ')}`}
          </div>
        </div>

        <div style={s.divider} />

        {/* Actions row */}
        <div style={s.row}>
          <button style={s.btn('outline', tedChecking)} onClick={checkTed} disabled={tedChecking}>
            {tedChecking ? 'Checking…' : 'Check TED availability'}
          </button>

          {tedResult && (
            <span style={s.tedResult(!tedResult.error)}>
              {tedResult.error
                ? `TED error: ${tedResult.error}`
                : `${tedResult.total.toLocaleString()} notices available on TED`}
            </span>
          )}
        </div>

        <div style={s.row}>
          <button style={s.btn('primary', ingesting)} onClick={triggerIngest} disabled={ingesting}>
            {ingesting ? 'Starting…' : `Run now — ${countryLabel}, ${maxTenders || '∞'} tenders`}
          </button>
          <button style={s.btn('secondary', false)} onClick={() => { loadRuns(); loadStats(); }}>
            Refresh
          </button>
          {msg && <span style={s.msg(msg.ok)}>{msg.text}</span>}
        </div>
      </div>

      {/* Runs table */}
      <div style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 10 }}>
        Recent runs
      </div>
      {loading ? (
        <p style={{ color: '#94a3b8', fontSize: 14 }}>Loading…</p>
      ) : runs.length === 0 ? (
        <div style={s.empty}>No ingestion runs yet.</div>
      ) : (
        <table style={s.table}>
          <thead>
            <tr>
              <th style={s.th}>Started</th>
              <th style={s.th}>Status</th>
              <th style={s.th}>Fetched</th>
              <th style={s.th}>Embedded</th>
              <th style={s.th}>Matches</th>
              <th style={s.th}>Duration</th>
              <th style={s.th}>Error</th>
            </tr>
          </thead>
          <tbody>
            {runs.map((r) => (
              <tr key={r.id}>
                <td style={s.td}>{formatDate(r.started_at)}</td>
                <td style={s.td}><span style={s.pill(r.status)}>{r.status}</span></td>
                <td style={s.td}>{r.tenders_fetched ?? '—'}</td>
                <td style={s.td}>{r.tenders_embedded ?? '—'}</td>
                <td style={s.td}>
                  {r.match_count ?? '—'}
                  {r.pending_match_count > 0 && (
                    <span style={{ fontSize: 11, color: '#f59e0b', marginLeft: 6, fontWeight: 600 }}>
                      +{r.pending_match_count} pending
                    </span>
                  )}
                </td>
                <td style={s.td}>{duration(r.started_at, r.completed_at)}</td>
                <td style={{ ...s.td, color: '#b91c1c', fontSize: 12 }}>{r.error || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
